'use client'

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ArrowUpRight } from 'lucide-react'
import NextLink from 'next/link'
import { HiArrowLeft, HiChevronDown, HiMiniXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Badge from '@/components/common/Badge'
import Chip from '@/components/common/Chip'
import IconButton from '@/components/common/IconButtonV2'
import Spinner from '@/components/common/Spinner'
import { Combobox } from '@/components/filter/Combobox'
import Code from '@/components/search/Code'
import { RecipientLink } from '@/components/usaspending/RecipientLink'

import { useRecipientAutocomplete } from '@/hooks/recipient-search'
import { useNAICSCodeSearch } from '@/hooks/search/useNAICSCodeSearch'
import { usePSCCodeSearch } from '@/hooks/search/usePSCCodeSearch'

import { buildRecipientId } from '@/lib/usaspending/recipientId'

import { useRecipientSearchStore } from '@/stores/RecipientSearchStore'

import {
  CERTIFICATION_BADGES,
  type CertificationType,
} from '@/types/recipient-search'

// Certification keys in display order
const CERTIFICATION_KEYS: CertificationType[] = [
  'is_8a',
  'hubzone',
  'sdvosb',
  'woman_owned',
  'veteran_owned',
  'sdb',
  'minority_owned',
  'small_business',
  'alaska_native',
  'native_american',
  'nonprofit',
  'educational',
]

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'PR', name: 'Puerto Rico' },
]

// --- Filter Section Component ---

/** Scroll element into view with breathing room at the bottom */
const scrollIntoViewWithOffset = (el: HTMLElement, offset: number = 24) => {
  const scrollParent = el.closest('.overflow-y-auto') as HTMLElement | null
  if (!scrollParent) return

  const elRect = el.getBoundingClientRect()
  const parentRect = scrollParent.getBoundingClientRect()

  const isAboveView = elRect.top < parentRect.top
  const isBelowView = elRect.bottom > parentRect.bottom - offset

  if (isAboveView) {
    // Scroll up to show element at top
    scrollParent.scrollBy({
      top: elRect.top - parentRect.top,
      behavior: 'smooth',
    })
  } else if (isBelowView) {
    // Scroll down to show element with offset from bottom
    scrollParent.scrollBy({
      top: elRect.bottom - parentRect.bottom + offset,
      behavior: 'smooth',
    })
  }
}

interface FilterSectionProps {
  title: string
  count: number
  onClear: () => void
  children: React.ReactNode
  defaultExpanded?: boolean
}

const FilterSection: React.FC<FilterSectionProps> = memo(
  ({ title, count, onClear, children, defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded)
    const contentRef = useRef<HTMLDivElement>(null)
    const isInitialMount = useRef(true)

    const handleToggle = useCallback(() => {
      setExpanded((prev) => !prev)
    }, [])

    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false
        return
      }
      if (expanded) {
        const timer = setTimeout(() => {
          if (contentRef.current) {
            scrollIntoViewWithOffset(contentRef.current, 24)
          }
        }, 210)
        return () => clearTimeout(timer)
      }
    }, [expanded])

    return (
      <div className="flex flex-col gap-y-1 px-4 py-2">
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
          className="flex w-full items-center justify-between text-primary-900 dark:text-primary-100 cursor-pointer select-none hover:bg-primary-50 dark:hover:bg-primary-800/50 rounded transition-colors"
        >
          <div className="flex items-center gap-x-2 min-w-0">
            <span className="font-semibold whitespace-nowrap truncate text-sm text-primary-900 dark:text-primary-100">
              {title}
            </span>
            {count > 0 && <Badge count={count} />}
          </div>
          <div className="flex items-center gap-x-1 text-primary-500 dark:text-primary-400">
            {count > 0 && (
              <IconButton
                as="div"
                type="button"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  onClear()
                }}
                className="py-0.5 px-0.5 hover:bg-primary-200 dark:hover:bg-primary-700"
                tooltip="Clear"
              >
                <HiMiniXMark className="h-4 w-4" />
              </IconButton>
            )}
            <HiChevronDown
              className={twMerge(
                'h-4 w-4 motion-safe:transition-transform motion-safe:duration-200',
                expanded && 'rotate-180',
              )}
            />
          </div>
        </button>
        <div
          ref={contentRef}
          className={twMerge(
            'motion-safe:transition-[max-height] motion-safe:duration-200',
            expanded
              ? 'max-h-[600px] overflow-visible'
              : 'max-h-0 overflow-hidden',
          )}
        >
          <div className="flex flex-col gap-y-2 pt-1 pb-3">{children}</div>
        </div>
      </div>
    )
  },
)

FilterSection.displayName = 'FilterSection'

// --- Certification Checkbox ---

interface CertificationCheckboxProps {
  type: CertificationType
  checked: boolean
  onChange: () => void
}

const CertificationCheckbox: React.FC<CertificationCheckboxProps> = memo(
  ({ type, checked, onChange }) => {
    const config = CERTIFICATION_BADGES.find((b) => b.type === type)
    if (!config) return null

    return (
      <label className="flex items-center gap-2 cursor-pointer group">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="w-4 h-4 rounded border-primary-300 dark:border-primary-600 text-accent-600 focus:ring-accent-500 dark:focus:ring-accent-400 bg-white dark:bg-primary-800"
        />
        <span
          className={twMerge(
            'text-sm transition-colors',
            checked
              ? 'text-primary-900 dark:text-primary-100 font-medium'
              : 'text-primary-600 dark:text-primary-400 group-hover:text-primary-900 dark:group-hover:text-primary-200',
          )}
        >
          {config.label}
        </span>
      </label>
    )
  },
)

CertificationCheckbox.displayName = 'CertificationCheckbox'

// --- State Combobox ---

interface StateComboboxProps {
  selectedStates: string[]
  onAdd: (state: string) => void
  onRemove: (state: string) => void
}

const StateCombobox: React.FC<StateComboboxProps> = memo(
  ({ selectedStates, onAdd, onRemove }) => {
    const [search, setSearch] = useState('')
    const [isFocused, setIsFocused] = useState(false)

    const filteredStates = useMemo(
      () =>
        US_STATES.filter(
          (state) =>
            !selectedStates.includes(state.code) &&
            (state.name.toLowerCase().includes(search.toLowerCase()) ||
              state.code.toLowerCase().includes(search.toLowerCase())),
        ),
      [selectedStates, search],
    )

    const handleSelect = useCallback(
      (code: string) => {
        onAdd(code)
        setSearch('')
        setIsFocused(false)
      },
      [onAdd],
    )

    return (
      <div className="flex flex-col gap-2">
        {/* Selected chips */}
        {selectedStates.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedStates.map((code) => {
              const state = US_STATES.find((s) => s.code === code)
              return (
                <Chip
                  key={code}
                  content={state?.name || code}
                  onDelete={() => onRemove(code)}
                />
              )
            })}
          </div>
        )}

        {/* Search input */}
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Search states..."
            className="w-full px-3 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 placeholder:text-primary-400 dark:placeholder:text-primary-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 dark:focus:ring-accent-400 dark:focus:border-accent-400"
          />

          {/* Dropdown */}
          {isFocused && filteredStates.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-primary-800 border border-primary-200 dark:border-primary-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {filteredStates.map((state) => (
                <button
                  key={state.code}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(state.code)}
                  className="w-full px-3 py-2 text-left text-sm text-primary-900 dark:text-primary-100 hover:bg-primary-100 dark:hover:bg-primary-700 transition-colors"
                >
                  {state.name} ({state.code})
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  },
)

StateCombobox.displayName = 'StateCombobox'

// --- Spending Range Input ---

interface SpendingRangeProps {
  minValue?: number
  maxValue?: number
  onChange: (min?: number, max?: number) => void
}

const SPENDING_PRESETS = [
  { label: '$1+', value: 1 },
  { label: '$1M+', value: 1000000 },
  { label: '$10M+', value: 10000000 },
  { label: '$100M+', value: 100000000 },
]

// Format number with commas for display
const formatWithCommas = (num: number | undefined): string => {
  if (num === undefined) return ''
  return num.toLocaleString('en-US')
}

const preventDefault = (e: React.MouseEvent) => e.preventDefault()

const SpendingRange: React.FC<SpendingRangeProps> = memo(
  ({ minValue, maxValue, onChange }) => {
    // Local draft state only used while focused
    const [minDraft, setMinDraft] = useState('')
    const [maxDraft, setMaxDraft] = useState('')
    const [focused, setFocused] = useState<'min' | 'max' | null>(null)

    const minInputRef = useRef<HTMLInputElement>(null)
    const maxInputRef = useRef<HTMLInputElement>(null)
    const presetClickedRef = useRef(false)

    const minDisplay = focused === 'min' ? minDraft : formatWithCommas(minValue)
    const maxDisplay = focused === 'max' ? maxDraft : formatWithCommas(maxValue)

    const handleFormattedChange = useCallback(
      (
        e: React.ChangeEvent<HTMLInputElement>,
        setDraft: (v: string) => void,
        inputRef: React.RefObject<HTMLInputElement | null>,
      ) => {
        const raw = e.target.value
        const cursorPos = e.target.selectionStart ?? raw.length
        const commasBefore = (raw.slice(0, cursorPos).match(/,/g) || []).length
        const digits = raw.replace(/\D/g, '')

        if (!digits) {
          setDraft('')
          return
        }

        const formatted = parseInt(digits, 10).toLocaleString('en-US')
        const commasAfter = (formatted.slice(0, cursorPos).match(/,/g) || [])
          .length
        const newCursor = cursorPos + (commasAfter - commasBefore)

        setDraft(formatted)

        requestAnimationFrame(() => {
          inputRef.current?.setSelectionRange(newCursor, newCursor)
        })
      },
      [],
    )

    const handleMinFocus = useCallback(() => {
      setMinDraft(formatWithCommas(minValue))
      setFocused('min')
    }, [minValue])

    const handleMaxFocus = useCallback(() => {
      setMaxDraft(formatWithCommas(maxValue))
      setFocused('max')
    }, [maxValue])

    const handleMinBlur = useCallback(() => {
      setFocused(null)
      if (presetClickedRef.current) return
      const num = minDraft
        ? parseInt(minDraft.replace(/,/g, ''), 10)
        : undefined
      onChange(num, maxValue)
    }, [minDraft, maxValue, onChange])

    const handleMaxBlur = useCallback(() => {
      setFocused(null)
      if (presetClickedRef.current) return
      const num = maxDraft
        ? parseInt(maxDraft.replace(/,/g, ''), 10)
        : undefined
      onChange(minValue, num)
    }, [maxDraft, minValue, onChange])

    const handlePresetClick = useCallback(
      (value: number) => {
        presetClickedRef.current = true
        const formatted = value.toLocaleString('en-US')
        const fillsMax =
          focused === 'max' || (!focused && minValue !== undefined)

        if (fillsMax) {
          const clearsMin = minValue !== undefined && value <= minValue
          if (clearsMin) {
            setMinDraft('')
          }
          setMaxDraft(formatted)
          onChange(clearsMin ? undefined : minValue, value)
          if (clearsMin) {
            requestAnimationFrame(() => minInputRef.current?.focus())
          }
        } else {
          const clearsMax = maxValue !== undefined && value >= maxValue
          if (clearsMax) {
            setMaxDraft('')
          }
          setMinDraft(formatted)
          onChange(value, clearsMax ? undefined : maxValue)
          requestAnimationFrame(() => maxInputRef.current?.focus())
        }
        requestAnimationFrame(() => {
          presetClickedRef.current = false
        })
      },
      [focused, minValue, maxValue, onChange],
    )

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500 text-sm">
              $
            </span>
            <input
              ref={minInputRef}
              type="text"
              value={minDisplay}
              onFocus={handleMinFocus}
              onChange={(e) =>
                handleFormattedChange(e, setMinDraft, minInputRef)
              }
              onBlur={handleMinBlur}
              placeholder="Min"
              className="w-full pl-6 pr-3 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 placeholder:text-primary-400 dark:placeholder:text-primary-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>
          <span className="text-primary-400 dark:text-primary-500">—</span>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400 dark:text-primary-500 text-sm">
              $
            </span>
            <input
              ref={maxInputRef}
              type="text"
              value={maxDisplay}
              onFocus={handleMaxFocus}
              onChange={(e) =>
                handleFormattedChange(e, setMaxDraft, maxInputRef)
              }
              onBlur={handleMaxBlur}
              placeholder="Max"
              className="w-full pl-6 pr-3 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 placeholder:text-primary-400 dark:placeholder:text-primary-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {SPENDING_PRESETS.map(({ label, value }) => {
            const isActive = minValue === value || maxValue === value
            const fillsMax =
              focused === 'max' || (!focused && minValue !== undefined)
            const wouldConflict = fillsMax
              ? minValue !== undefined && value <= minValue
              : maxValue !== undefined && value >= maxValue
            return (
              <button
                key={label}
                type="button"
                onMouseDown={preventDefault}
                onClick={() => handlePresetClick(value)}
                className={twMerge(
                  'px-2 py-1 text-xs rounded-md border transition-colors',
                  isActive
                    ? 'bg-accent-100 dark:bg-accent-900/40 text-accent-700 dark:text-accent-300 border-accent-300 dark:border-accent-700'
                    : 'bg-primary-50 dark:bg-primary-800 text-primary-600 dark:text-primary-400 border-primary-200 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-700',
                  wouldConflict && 'opacity-40',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>
    )
  },
)

SpendingRange.displayName = 'SpendingRange'

// --- Parent Company Typeahead ---

interface ParentCompanyTypeaheadProps {
  onSelect: (hash: string, name: string) => void
}

const ParentCompanyTypeahead: React.FC<ParentCompanyTypeaheadProps> = memo(
  ({ onSelect }) => {
    const [search, setSearch] = useState('')
    const [isFocused, setIsFocused] = useState(false)

    const { data: suggestions, isLoading } = useRecipientAutocomplete(search, {
      enabled: search.length >= 2,
      limit: 8,
      parentsOnly: true, // Only show companies that have subsidiaries
    })

    const handleSelect = useCallback(
      (hash: string, name: string) => {
        onSelect(hash, name)
        setSearch('')
        setIsFocused(false)
      },
      [onSelect],
    )

    const showDropdown = isFocused && search.length >= 2

    return (
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Search parent company..."
          className="w-full px-3 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 placeholder:text-primary-400 dark:placeholder:text-primary-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 dark:focus:ring-accent-400 dark:focus:border-accent-400"
        />

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-primary-800 border border-primary-200 dark:border-primary-700 rounded-md shadow-lg max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Spinner className="w-4 h-4" />
              </div>
            ) : suggestions && suggestions.length > 0 ? (
              suggestions.map((s) => (
                <button
                  key={s.recipient_hash}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() =>
                    handleSelect(s.recipient_hash, s.name || 'Unknown')
                  }
                  className="w-full px-3 py-2 text-left hover:bg-primary-100 dark:hover:bg-primary-700 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-primary-900 dark:text-primary-100 truncate">
                      {s.name || 'Unknown'}
                    </div>
                    {s.subsidiary_count !== undefined &&
                      s.subsidiary_count > 0 && (
                        <span className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-accent-100 dark:bg-accent-900/40 text-accent-700 dark:text-accent-300">
                          {s.subsidiary_count}{' '}
                          {s.subsidiary_count === 1 ? 'sub' : 'subs'}
                        </span>
                      )}
                  </div>
                  <div className="text-xs text-primary-500 dark:text-primary-400">
                    {[s.city, s.state].filter(Boolean).join(', ')}
                    {s.uei && ` · ${s.uei}`}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-primary-500 dark:text-primary-400 text-center">
                No companies found
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
)

ParentCompanyTypeahead.displayName = 'ParentCompanyTypeahead'

// Stable accessor for Combobox itemIdFn (NAICS and PSC both use .code)
const getItemCode = (item: { code: string }) => item.code

// --- Code Input (for Agencies - manual entry until agency autocomplete is built) ---

interface CodeInputProps {
  codes: string[]
  onAdd: (code: string) => void
  onRemove: (code: string) => void
  placeholder: string
}

const CodeInput: React.FC<CodeInputProps> = memo(
  ({ codes, onAdd, onRemove, placeholder }) => {
    const [input, setInput] = useState('')

    const handleAdd = useCallback(() => {
      const trimmed = input.trim()
      if (trimmed && !codes.includes(trimmed)) {
        onAdd(trimmed)
        setInput('')
      }
    }, [input, codes, onAdd])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          handleAdd()
        }
      },
      [handleAdd],
    )

    return (
      <div className="flex flex-col gap-2">
        {codes.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {codes.map((code) => (
              <Chip key={code} content={code} onDelete={() => onRemove(code)} />
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 placeholder:text-primary-400 dark:placeholder:text-primary-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!input.trim()}
            className="px-3 py-2 text-sm font-medium text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      </div>
    )
  },
)

CodeInput.displayName = 'CodeInput'

// --- Date Range Input ---

interface DateRangeInputProps {
  minValue?: string
  maxValue?: string
  onChange: (min?: string, max?: string) => void
}

const DateRangeInput: React.FC<DateRangeInputProps> = memo(
  ({ minValue, maxValue, onChange }) => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-1 min-w-0">
            <label className="block text-xs text-primary-500 dark:text-primary-400 mb-1">
              From
            </label>
            <input
              type="date"
              value={minValue || ''}
              onChange={(e) => onChange(e.target.value || undefined, maxValue)}
              className="w-full min-w-0 px-2 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>
          <div className="flex-1 min-w-0">
            <label className="block text-xs text-primary-500 dark:text-primary-400 mb-1">
              To
            </label>
            <input
              type="date"
              value={maxValue || ''}
              onChange={(e) => onChange(minValue, e.target.value || undefined)}
              className="w-full min-w-0 px-2 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>
        </div>
      </div>
    )
  },
)

DateRangeInput.displayName = 'DateRangeInput'

// --- Main Filter Panel ---

interface RecipientFiltersProps {
  className?: string
}

const RecipientFilters: React.FC<RecipientFiltersProps> = ({ className }) => {
  const {
    filters,
    parentRecipient,
    parentRecipientName,
    getActiveFilterCount,
    clearAllFilters,
    clearParentRecipient,
    toggleCertification,
    addState,
    removeState,
    addNaicsCode,
    removeNaicsCode,
    addPscCode,
    removePscCode,
    addAgency,
    removeAgency,
    setSpendingRange,
    setAwardsRange,
    setLastAwardDateRange,
    setParentRecipient,
    setFilters,
  } = useRecipientSearchStore()

  // NAICS autocomplete
  const {
    query: naicsQuery,
    setQuery: setNAICSQuery,
    data: naicsResults,
  } = useNAICSCodeSearch({ initialCodes: filters.naics_codes })

  // PSC autocomplete
  const {
    query: pscQuery,
    setQuery: setPSCQuery,
    data: pscResults,
  } = usePSCCodeSearch({ initialCodes: filters.psc_codes })

  const activeFilterCount = getActiveFilterCount()

  // Compute active and inactive certifications
  const { activeCerts, inactiveCerts, certCount } = useMemo(() => {
    const active = CERTIFICATION_KEYS.filter((key) => filters[key])
    const inactive = CERTIFICATION_KEYS.filter((key) => !filters[key])
    return {
      activeCerts: active,
      inactiveCerts: inactive,
      certCount: active.length,
    }
  }, [filters])

  // Count spending filters
  const spendingCount =
    (filters.min_spending !== undefined ? 1 : 0) +
    (filters.max_spending !== undefined ? 1 : 0)

  // Count award filters
  const awardCount =
    (filters.min_awards !== undefined ? 1 : 0) +
    (filters.max_awards !== undefined ? 1 : 0)

  return (
    <div className={twMerge('flex flex-col h-full', className)}>
      {/* Subsidiary banner - shown when viewing subsidiaries of a parent */}
      {parentRecipient && parentRecipientName && (
        <div className="w-full bg-accent-50 dark:bg-accent-900/20 border-b border-accent-200 dark:border-accent-800 px-4 py-3">
          {/* Back button - clears parent filter */}
          <button
            type="button"
            onClick={clearParentRecipient}
            className="flex items-center gap-2 mb-1 cursor-pointer hover:opacity-70 transition-opacity"
          >
            <span className="p-1 -ml-1 text-accent-600 dark:text-accent-400">
              <HiArrowLeft className="w-4 h-4" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide text-accent-600 dark:text-accent-400">
              Viewing Subsidiaries
            </span>
          </button>

          {/* Parent name link */}
          <RecipientLink
            hash={parentRecipient}
            level="P"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-accent-900 dark:text-accent-100 hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors"
          >
            <span className="truncate">{parentRecipientName}</span>
            <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover:opacity-70 transition-opacity" />
          </RecipientLink>
        </div>
      )}

      {/* Header */}
      <div className="sticky -top-4 bg-white dark:bg-primary-900 z-10 px-4 py-[21px] border-b border-primary-200 dark:border-primary-700">
        <div className="flex items-center justify-between">
          <span className="text-md font-bold text-primary-900 dark:text-primary-100">
            Filters
          </span>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-sm text-accent-600 dark:text-accent-400 hover:text-accent-700 dark:hover:text-accent-300 font-medium"
            >
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto pt-1 pb-24 divide-y divide-primary-200/80 dark:divide-primary-800">
        {/* Parent Company (for finding subsidiaries) */}
        {!parentRecipient && (
          <FilterSection
            title="Find Subsidiaries"
            count={0}
            onClear={() => {}}
            defaultExpanded={false}
          >
            <p className="text-xs text-primary-500 dark:text-primary-400 mb-2">
              Search for a parent company to view its subsidiaries
            </p>
            <ParentCompanyTypeahead onSelect={setParentRecipient} />
          </FilterSection>
        )}

        {/* Location */}
        <FilterSection
          title="Location"
          count={filters.states.length}
          onClear={() => setFilters({ states: [], cities: [] })}
        >
          <StateCombobox
            selectedStates={filters.states}
            onAdd={addState}
            onRemove={removeState}
          />
        </FilterSection>

        {/* Certifications */}
        <FilterSection
          title="Certifications"
          count={certCount}
          onClear={() =>
            setFilters({
              woman_owned: undefined,
              veteran_owned: undefined,
              sdvosb: undefined,
              is_8a: undefined,
              hubzone: undefined,
              sdb: undefined,
              minority_owned: undefined,
              small_business: undefined,
              alaska_native: undefined,
              native_american: undefined,
              nonprofit: undefined,
              educational: undefined,
            })
          }
        >
          <div className="flex flex-col gap-2">
            {/* Active certifications at top */}
            {activeCerts.map((type) => (
              <CertificationCheckbox
                key={type}
                type={type}
                checked={true}
                onChange={() => toggleCertification(type)}
              />
            ))}
            {/* Divider if there are active filters */}
            {certCount > 0 && (
              <div className="border-t border-primary-200 dark:border-primary-700 my-1" />
            )}
            {/* Inactive certifications */}
            {inactiveCerts.map((type) => (
              <CertificationCheckbox
                key={type}
                type={type}
                checked={false}
                onChange={() => toggleCertification(type)}
              />
            ))}
          </div>
        </FilterSection>

        {/* Spending Range */}
        <FilterSection
          title="Awards (Last 12 Mo)"
          count={spendingCount}
          onClear={() => setSpendingRange(undefined, undefined)}
        >
          <SpendingRange
            minValue={filters.min_spending}
            maxValue={filters.max_spending}
            onChange={setSpendingRange}
          />
        </FilterSection>

        {/* NAICS & PSC Codes */}
        <FilterSection
          title="NAICS & PSC Codes"
          count={filters.naics_codes.length + filters.psc_codes.length}
          onClear={() => setFilters({ naics_codes: [], psc_codes: [] })}
          defaultExpanded={true}
        >
          <div className="flex flex-col gap-3">
            <div>
              <label className="block text-xs font-medium text-primary-500 dark:text-primary-400 mb-1.5">
                NAICS Codes
              </label>
              <div className="flex flex-col gap-2">
                <Combobox
                  query={naicsQuery}
                  options={naicsResults}
                  selectedIds={filters.naics_codes}
                  placeholder="Ex: 541511"
                  itemIdFn={getItemCode}
                  itemDisplayFn={(code) => (
                    <Code code={code.code} description={code.description} />
                  )}
                  setQuery={setNAICSQuery}
                  onAdd={(code) => addNaicsCode(code.code)}
                  onRemove={(code) => removeNaicsCode(code.code)}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-primary-500 dark:text-primary-400 mb-1.5">
                PSC Codes
              </label>
              <div className="flex flex-col gap-2">
                <Combobox
                  query={pscQuery}
                  options={pscResults}
                  selectedIds={filters.psc_codes}
                  placeholder="Ex: R425"
                  itemIdFn={getItemCode}
                  itemDisplayFn={(code) => (
                    <Code code={code.code} description={code.description} />
                  )}
                  setQuery={setPSCQuery}
                  onAdd={(code) => addPscCode(code.code)}
                  onRemove={(code) => removePscCode(code.code)}
                />
              </div>
            </div>
          </div>
        </FilterSection>

        {/* Agencies */}
        <FilterSection
          title="Awarding Agencies"
          count={filters.agencies.length}
          onClear={() => setFilters({ agencies: [] })}
          defaultExpanded={false}
        >
          <CodeInput
            codes={filters.agencies}
            onAdd={addAgency}
            onRemove={removeAgency}
            placeholder="Enter agency name..."
          />
        </FilterSection>

        {/* Last Award Date Range */}
        <FilterSection
          title="Last Award Date"
          count={
            (filters.min_last_award_date ? 1 : 0) +
            (filters.max_last_award_date ? 1 : 0)
          }
          onClear={() => setLastAwardDateRange(undefined, undefined)}
          defaultExpanded={false}
        >
          <DateRangeInput
            minValue={filters.min_last_award_date}
            maxValue={filters.max_last_award_date}
            onChange={setLastAwardDateRange}
          />
        </FilterSection>

        {/* Award Count Range */}
        <FilterSection
          title="Award Count (Last 12 Months)"
          count={awardCount}
          onClear={() => setAwardsRange(undefined, undefined)}
          defaultExpanded={false}
        >
          <div className="flex items-center gap-2 min-w-0">
            <input
              type="number"
              value={filters.min_awards ?? ''}
              onChange={(e) =>
                setAwardsRange(
                  e.target.value ? parseInt(e.target.value) : undefined,
                  filters.max_awards,
                )
              }
              placeholder="Min"
              className="flex-1 min-w-0 px-2 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 placeholder:text-primary-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
            <span className="text-primary-400 flex-shrink-0">—</span>
            <input
              type="number"
              value={filters.max_awards ?? ''}
              onChange={(e) =>
                setAwardsRange(
                  filters.min_awards,
                  e.target.value ? parseInt(e.target.value) : undefined,
                )
              }
              placeholder="Max"
              className="flex-1 min-w-0 px-2 py-2 text-sm border border-primary-200 dark:border-primary-700 rounded-md bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-100 placeholder:text-primary-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            />
          </div>
        </FilterSection>

        {/* Company Type */}
        <FilterSection
          title="Company Type"
          count={filters.has_parent !== undefined ? 1 : 0}
          onClear={() => setFilters({ has_parent: undefined })}
          defaultExpanded={false}
        >
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="company_type"
                checked={filters.has_parent === undefined}
                onChange={() => setFilters({ has_parent: undefined })}
                className="w-4 h-4 border-primary-300 dark:border-primary-600 text-accent-600 focus:ring-accent-500"
              />
              <span className="text-sm text-primary-600 dark:text-primary-400 group-hover:text-primary-900 dark:group-hover:text-primary-200">
                All Companies
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="company_type"
                checked={filters.has_parent === false}
                onChange={() => setFilters({ has_parent: false })}
                className="w-4 h-4 border-primary-300 dark:border-primary-600 text-accent-600 focus:ring-accent-500"
              />
              <span className="text-sm text-primary-600 dark:text-primary-400 group-hover:text-primary-900 dark:group-hover:text-primary-200">
                Parent Companies Only
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name="company_type"
                checked={filters.has_parent === true}
                onChange={() => setFilters({ has_parent: true })}
                className="w-4 h-4 border-primary-300 dark:border-primary-600 text-accent-600 focus:ring-accent-500"
              />
              <span className="text-sm text-primary-600 dark:text-primary-400 group-hover:text-primary-900 dark:group-hover:text-primary-200">
                Subsidiaries Only
              </span>
            </label>
          </div>
        </FilterSection>
      </div>
    </div>
  )
}

export default memo(RecipientFilters)
